import classnames from 'classnames';
import { MetaConversation } from 'lib-shared-legacy-types';
import { useCallback, useLayoutEffect, useMemo, useState } from 'react';

import { AddContactAttributesModal } from 'components/contact';
import { SidebarToggleTrigger } from 'components/conversation/components/sidebar/sidebarToggleTrigger';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useServerStorage } from 'utils/useServerStorage';

import { SuggestionCards } from './cards/KnowledgeCard';
import { CustomIntegrationCards } from './components/customIntegrationCards/customIntegrationCards';
import { CustomerInfoCards } from './components/customerInfoCards/customerInfoCards';
import { IntegrationCards } from './components/integrationCards/integrationCards';
import type {
  SidebarMode,
  SidebarSectionId,
} from './conversationSidebar.types';
import { defaultSectionOrder } from './conversationSidebar.types';
import { SidebarModeContext } from './hooks/useSidebarMode';

type CustomCardId = string;
type CardIsOpen = boolean;
export type ConversationCardsSettings = Record<CustomCardId, CardIsOpen>;

type ConversationSidebarProps = {
  metaConversation: MetaConversation;
  conversationCardsSettings: ConversationCardsSettings;
  isMenuOpen: boolean;
  toggleCustomCardMenu: (arg: {
    key: string;
    value: boolean;
  }) => Promise<unknown>;
  updateConversationCards: (arg: {
    key: string;
    value: boolean;
  }) => Promise<unknown>;
};

const ConversationSidebar = ({
  metaConversation,
  conversationCardsSettings,
  updateConversationCards,
  isMenuOpen,
  toggleCustomCardMenu,
}: ConversationSidebarProps): JSX.Element => {
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>('default');
  const storage = useServerStorage('user');
  const sectionOrder = useMemo(
    () =>
      Array.from(
        new Set([
          ...(storage.data.conversation?.sidebarSectionOrder ?? []),
          ...defaultSectionOrder,
        ]),
      ),
    [storage.data.conversation?.sidebarSectionOrder],
  );

  const isSmallLayout = useBreakpointValue({
    base: true,
    lg: false,
  });

  const isCardOpen = useCallback(
    (key: string) => conversationCardsSettings[key] ?? false,
    [conversationCardsSettings],
  );

  const toggleCard = useCallback(
    (key: string) => {
      updateConversationCards({ key, value: !isCardOpen(key) });
    },
    [updateConversationCards, isCardOpen],
  );

  const toggleSidebar = useCallback(() => {
    toggleCustomCardMenu({
      key: 'isCustomCardMenuOpen',
      value: !isMenuOpen,
    });
  }, [isMenuOpen, toggleCustomCardMenu]);

  useLayoutEffect(() => {
    if (isSmallLayout) {
      toggleCustomCardMenu({
        key: 'isCustomCardMenuOpen',
        value: false,
      });
    }
  }, [isSmallLayout, toggleCustomCardMenu]);

  const rootClassName = classnames('conversation-sidebar-holder', {
    closed: !isMenuOpen,
  });

  const renderCardSection = (type: SidebarSectionId, index: number) => {
    switch (type) {
      case 'customerInfo':
        return (
          <CustomerInfoCards
            key={type}
            index={index}
            isMinimized={!isMenuOpen}
            metaConversation={metaConversation}
            isCardOpen={isCardOpen}
            toggleCard={toggleCard}
          />
        );

      case 'knowledge':
        return (
          <SuggestionCards key={type} index={index} isMinimized={!isMenuOpen} />
        );
      case 'nativeIntegration':
        return (
          <IntegrationCards
            key={type}
            index={index}
            isMinimized={!isMenuOpen}
            metaConversation={metaConversation}
            isCardOpen={isCardOpen}
            toggleCard={toggleCard}
          />
        );
      case 'customIntegration':
        return (
          <CustomIntegrationCards
            key={type}
            index={index}
            isMinimized={!isMenuOpen}
            metaConversation={metaConversation}
          />
        );
    }
  };

  return (
    <div className={rootClassName}>
      <SidebarToggleTrigger
        isMenuOpen={isMenuOpen}
        toggleSidebar={toggleSidebar}
        sidebarMode={sidebarMode}
        setSidebarMode={setSidebarMode}
      />
      <SidebarModeContext.Provider value={sidebarMode}>
        {sectionOrder.map((id, index) => renderCardSection(id, index))}
      </SidebarModeContext.Provider>
      <AddContactAttributesModal />
    </div>
  );
};

export default ConversationSidebar;
